import $ from "jquery";
import "@accessible360/accessible-slick";

export default class Slider {
    constructor(selector = ".el-images", options = {}) {
        this.$wrappers = $(selector);

        this.defaults = {
            slidesToShow: 6,
            infinite: true,
            arrows: true,

            prevArrow: typeof window.prevArrow !== "undefined" ? window.prevArrow : null,
            nextArrow: typeof window.nextArrow !== "undefined" ? window.nextArrow : null,

            autoplay: true,
            autoplaySpeed: 2500,
            speed: 600,
            cssEase: "ease",

            pauseOnHover: true,
            pauseOnFocus: true,
            pauseOnDotsHover: true,

            swipe: true,
            draggable: true,
            touchMove: true,

            responsive: [
                {
                    breakpoint: 1024,
                    settings: {
                        slidesToShow: 4
                    }
                },
                {
                    breakpoint: 768,
                    settings: {
                        slidesToShow: 2
                    }
                }
            ]
        };

        this.options = {
            ...this.defaults,
            ...options
        };

        this.init();
    }

    init() {
        if (!this.$wrappers.length) {
            return;
        }

        this.$wrappers.each((index, element) => {
            const $wrapper = $(element);
            const $list = $wrapper.find(".el-images__wrapper");

            if (!$list.length || $list.hasClass("slick-initialized")) {
                return;
            }

            const settings = this.getSettingsFromData($wrapper);

            if (this.prefersReducedMotion()) {
                settings.autoplay = false;
                settings.speed = 300;
                settings.cssEase = "ease";
                settings.pauseOnHover = true;
                settings.pauseOnFocus = true;
                settings.swipe = true;
                settings.draggable = true;
                settings.touchMove = true;
            }

            $list.slick(settings);

            if (settings._marqueeTicker) {
                const $button = this.ensurePauseButton($wrapper);
                this.bindMarqueeTicker($list, $wrapper, $button);
            }
        });
    }

    getSettingsFromData($wrapper) {
        const type = String(this.readData($wrapper, ["slide-typ"]) || "").toLowerCase();
        const marqueeStop = this.readData($wrapper, ["marquee-stop"]);

        const rawAutoplay = this.readData($wrapper, ["slide-autoplay"]);
        const rawSpeed = this.readData($wrapper, ["slide-speed"]);
        const rawAutoplaySpeed = this.readData($wrapper, [
            "slide-autoplaySpeed",
            "slide-autoplayspeed",
            "slide-autoplay-speed"
        ]);
        const rawSlides = this.readData($wrapper, ["slides", "slide-slides"]);

        const speed = parseInt(rawSpeed, 10);
        const autoplaySpeed = parseInt(rawAutoplaySpeed, 10);
        const slidesToShow = parseInt(rawSlides, 10);

        const settings = {
            ...this.options,
            autoplay: rawAutoplay === true || rawAutoplay === "true" ? true : this.options.autoplay,
            speed: Number.isFinite(speed) ? speed : this.options.speed,
            autoplaySpeed: Number.isFinite(autoplaySpeed) ? autoplaySpeed : this.options.autoplaySpeed,
            slidesToShow: Number.isFinite(slidesToShow) ? slidesToShow : this.options.slidesToShow
        };

        if (type === "marquee") {
            settings.autoplay = false;
            settings.autoplaySpeed = 0;
            settings.speed = 300;
            settings.cssEase = "linear";
            settings.infinite = true;

            settings.pauseOnHover = false;
            settings.pauseOnFocus = false;
            settings.pauseOnDotsHover = false;

            settings.swipe = false;
            settings.draggable = false;
            settings.touchMove = false;

            if (marqueeStop === "exact" || marqueeStop === true || marqueeStop === "true") {
                settings._marqueeTicker = true;
            }
        }

        return settings;
    }

    ensurePauseButton($wrapper) {
        let $button = $wrapper.find(".slick-autoplay-toggle-button").first();

        if ($button.length) {
            return $button;
        }

        const html = `
<button type="button" class="slick-autoplay-toggle-button" aria-pressed="false">
    <span class="slick-pause-icon" aria-hidden="true"></span>
    <span class="slick-play-icon" aria-hidden="true" style="display: none;"></span>
    <span class="slick-pause-text slick-sr-only">Pause</span>
    <span class="slick-play-text slick-sr-only" style="display: none;">Play</span>
</button>`;

        const $target = $wrapper.find(".el-images__wrapper").first();

        if ($target.length) {
            $target.prepend(html);
            return $target.find(".slick-autoplay-toggle-button").first();
        }

        $wrapper.prepend(html);
        return $wrapper.find(".slick-autoplay-toggle-button").first();
    }

    bindMarqueeTicker($list, $wrapper, $button) {
        const $track = $list.find(".slick-track");

        if (!$track.length || this.prefersReducedMotion()) {
            return;
        }

        const rawPps = parseFloat(this.readData($wrapper, ["marquee-pps", "marqueePps"]));
        const pixelsPerSecond = Number.isFinite(rawPps) ? rawPps : 35;

        const direction = String(
            this.readData($wrapper, ["marquee-dir", "marquee-direction"]) || "rtl"
        ).toLowerCase();

        const sign = direction === "ltr" ? 1 : -1;

        const $pauseIcon = $button.find(".slick-pause-icon");
        const $playIcon = $button.find(".slick-play-icon");
        const $pauseText = $button.find(".slick-pause-text");
        const $playText = $button.find(".slick-play-text");

        let rafId = null;
        let lastTimestamp = null;
        let offsetX = 0;
        let paused = false;

        const getLoopWidth = () => {
            const width = $track[0].scrollWidth || $track.width();
            return width ? width / 2 : 0;
        };

        const applyTransform = () => {
            $track.css({
                transition: "none",
                willChange: "transform",
                transform: `translate3d(${offsetX}px, 0, 0)`
            });
        };

        const setButtonState = (isPaused) => {
            $pauseIcon.css("display", isPaused ? "none" : "");
            $playIcon.css("display", isPaused ? "" : "none");
            $pauseText.css("display", isPaused ? "none" : "");
            $playText.css("display", isPaused ? "" : "none");
            $button.attr("aria-pressed", isPaused ? "true" : "false");
        };

        const tick = (timestamp) => {
            if (paused) {
                return;
            }

            if (lastTimestamp === null) {
                lastTimestamp = timestamp;
            }

            const delta = (timestamp - lastTimestamp) / 1000;
            lastTimestamp = timestamp;

            offsetX += sign * pixelsPerSecond * delta;

            const loopWidth = getLoopWidth();

            if (loopWidth > 0) {
                while (offsetX <= -loopWidth) {
                    offsetX += loopWidth;
                }

                while (offsetX > 0) {
                    offsetX -= loopWidth;
                }
            }

            applyTransform();
            rafId = requestAnimationFrame(tick);
        };

        const play = () => {
            paused = false;
            lastTimestamp = null;

            if (!rafId) {
                rafId = requestAnimationFrame(tick);
            }

            setButtonState(false);
        };

        const pause = () => {
            paused = true;

            if (rafId) {
                cancelAnimationFrame(rafId);
            }

            rafId = null;
            lastTimestamp = null;

            applyTransform();
            setButtonState(true);
        };

        setButtonState(false);
        play();

        $button.off("click.sliderMarqueeExact").on("click.sliderMarqueeExact", (event) => {
            event.preventDefault();

            const isPaused = $button.attr("aria-pressed") === "true";

            if (isPaused) {
                play();
            } else {
                pause();
            }
        });

        $list.off("setPosition.sliderMarqueeExact").on("setPosition.sliderMarqueeExact", () => {
            applyTransform();
        });

        $(window).off("resize.sliderMarqueeExact").on("resize.sliderMarqueeExact", () => {
            applyTransform();
        });
    }

    readData($element, keys = []) {
        for (const key of keys) {
            const value = $element.data(key);

            if (value !== undefined) {
                return value;
            }
        }

        for (const key of keys) {
            const value = $element.attr(`data-${key}`);

            if (value !== undefined) {
                return value;
            }
        }

        return undefined;
    }

    prefersReducedMotion() {
        return (
            window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
        );
    }
}